/**
 * 과제 스케줄 유틸
 *
 * 반복 배포: dayOfWeek + interval로 effective_date 기간 내 날짜를 순차 배정
 * 단일 배포: date 필드가 있으면 고정일로 처리
 */
import { parseDate } from "./dateUtils";

export const getLastTrueDay = (dayOfWeek: boolean[]): number => {
    for (let i = 6; i >= 0; i--) {
        if (dayOfWeek[i] === true) return i;
    }
    return -1;
};

export const parseDateString = (dateString: string): Date | null => {
    if (typeof dateString !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return null;
    const [year, month, day] = dateString.split("-").map(Number);
    const date = new Date(0);
    date.setHours(0, 0, 0, 0);
    date.setFullYear(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
        return null;
    }
    return date;
};

export const getDateRangeStart = (monthsAgo: number, fromDate: Date = new Date()): string => {
    const targetDate = new Date(fromDate);
    targetDate.setMonth(fromDate.getMonth() + monthsAgo);
    return parseDate(targetDate);
};

interface ReportGroupBase {
    release_id: string;
    distribution_id?: string;
    effective_date: { start: string; end: string };
    dayOfWeek: boolean[];
    interval: number;
    order: number;
    date: Date | null | string;
    report: {
        path: string;
        id: string;
        title: string;
        folderPath: string[];
    };
    progress?: number;
    report_setting?: unknown;
}

/** 반복 스케줄끼리 묶고, 고정일 과제는 별도 그룹으로 분리 */
export const groupReportsBySchedule = <T extends ReportGroupBase>(reports: T[]) => {
    const repeatGroups: Record<string, T[]> = {};
    const singleDateGroup: T[] = [];

    reports.forEach((report) => {
        if (report.date) {
            singleDateGroup.push(report);
        } else {
            // 새 배포는 배포 식별자로 구분하고, 기존 데이터는 같은 스케줄끼리 묶는다.
            const schedule = `${report.effective_date.start}_${report.interval}_${report.dayOfWeek.join("")}`;
            const key = report.distribution_id
                ? `distribution:${report.distribution_id}_${schedule}`
                : `legacy:${schedule}`;
            if (!repeatGroups[key]) repeatGroups[key] = [];
            repeatGroups[key].push(report);
        }
    });

    return [
        ...Object.values(repeatGroups),
        singleDateGroup.length > 0 ? singleDateGroup : null,
    ].filter(Boolean) as T[][];
};

/** dayOfWeek가 true인 요일에 reportList 순서대로 날짜를 배정. interval주마다 다음 주기로 이동 */
export const assignTaskDates = <T extends object>(
    reportList: T[],
    dayOfWeek: boolean[],
    startDateStr: string,
    interval = 1
): (T & { assignedDate: Date })[] => {
    const currentDate = parseDateString(startDateStr);
    if (
        !currentDate || !Array.isArray(dayOfWeek) || dayOfWeek.length !== 7 ||
        !dayOfWeek.every(day => typeof day === "boolean") ||
        !dayOfWeek.some(day => day === true) || !Number.isInteger(interval) || interval < 1
    ) {
        return [];
    }
    const assigned: (T & { assignedDate: Date })[] = [];
    let reportIdx = 0;

    const lastTrueDay = getLastTrueDay(dayOfWeek);

    while (reportIdx < reportList.length && Number.isFinite(currentDate.getTime())) {
        for (let dow = 0; dow < 7 && reportIdx < reportList.length; dow++) {
            const dayIdx = currentDate.getDay();
            if (dayOfWeek[dayIdx] === true) {
                assigned.push({
                    ...reportList[reportIdx],
                    assignedDate: new Date(currentDate),
                });
                reportIdx++;
                if (dayIdx === lastTrueDay) {
                    currentDate.setDate(currentDate.getDate() + (7 - dayIdx));
                    currentDate.setDate(currentDate.getDate() + (interval - 1) * 7);
                    break;
                }
            }
            currentDate.setDate(currentDate.getDate() + 1);
        }
    }
    return assigned;
};
